import type { AiInsightTone } from '../../components/AiInsight';
import type { ScriptedStep } from '../../components/useScriptedSteps';
import { COMPANY, getPerson } from '../../data/company';
import { getClause } from '../../data/clauses';
import { getContract } from '../../data/contracts';
import { addDays, fmtDate } from '../../data/dates';
import { fmtMoney } from '../../data/format';
import { getSupplier } from '../../data/suppliers';
import {
  earlyPayDeadline,
  getAnalyses,
  type CheckResult,
  type InvoiceAnalysis,
} from './compliance-engine';
import type { EmailDraft } from './compliance-store';
import type { InvoiceId } from './invoices-data';

/**
 * Per-invoice recommended resolution: the headline the agent proposes, the
 * scripted steps it runs on approval, the drafted supplier email and the
 * simulated follow-up thread. All dollar figures come from the engine.
 */

export type PlanKind =
  | 'dispute'
  | 'credit_request'
  | 'early_pay'
  | 'released'
  | 'terms_corrected';

export interface FollowUpMessage {
  from: 'agent' | 'supplier';
  author: string;
  body: string;
  whenLabel: string;
}

export interface FollowUpScript {
  /** First thread message, posted when the notice goes out. */
  sent: FollowUpMessage;
  /** Supplier reply and agent note a few hours later. */
  acknowledged: FollowUpMessage[];
  /** Corrected document received and re-checked two days later. */
  closed: FollowUpMessage[];
}

export interface ResolutionPlan {
  invoiceId: InvoiceId;
  kind: PlanKind;
  tone: AiInsightTone;
  headline: string;
  rationale: string;
  /** What the agent does when approved. */
  actions: string[];
  primaryLabel: string;
  steps: ScriptedStep[];
  /** Amount scheduled for payment. */
  payNow: number;
  /** Amount held or short-paid. */
  withheld: number;
  payOn: Date;
  /** Agent action shown in the queue before approval. */
  draftedLabel: string;
  /** Agent action once approved and waiting on the supplier. */
  sentLabel: string;
  /** Agent action once the supplier has fixed it. */
  closedLabel: string;
  /** Summary shown after the agent has run. */
  outcome: string;
  emailLabel: string | null;
  email: EmailDraft | null;
  followUp: FollowUpScript | null;
  /** The agent already released the invoice; nothing to approve. */
  autoReleased: boolean;
  related: { label: string; alert: string } | null;
}

const STEP_MS = 900;

export const PLAN_STEP_MS = STEP_MS;

function usd(value: number): string {
  return fmtMoney(value, { cents: true });
}

const DISPUTE_REF: Record<InvoiceId, string> = {
  'CV-2026-08812': 'DSP-2026-0612',
  'LB-26-3391': 'DSP-2026-0613',
  'HP-INV-55102': 'DSP-2026-0614',
  'TCF-778120': 'DSP-2026-0615',
  'STR-INV-20931': 'DSP-2026-0616',
  'KS-104477': 'DSP-2026-0617',
  'NGF-22871': 'DSP-2026-0618',
  'GAI-2026-0912': 'DSP-2026-0619',
  'AFP-90233': 'DSP-2026-0620',
  'BLC-3318': 'DSP-2026-0621',
  'AIG-90412': 'DSP-2026-0622',
  'BS-22188': 'DSP-2026-0623',
  'BVL-7741': 'DSP-2026-0624',
  'SF-3398': 'DSP-2026-0625',
};

function nteAmount(): number {
  const rule = getClause('SOW-HALB-2026-03:5.2')?.rule;
  return rule?.kind === 'not_to_exceed' ? rule.amount : 0;
}

function rebateThreshold(): number {
  const rule = getClause('SA-NGTE-2024-012:2.2')?.rule;
  return rule?.kind === 'rebate' ? rule.threshold : 0;
}

function impact(a: InvoiceAnalysis, category: CheckResult['category']): number {
  return (
    a.flagged.find((entry) => entry.category === category)?.impactAmount ?? 0
  );
}

function ownerName(a: InvoiceAnalysis): string {
  return getPerson(getContract(a.invoice.contractIds[0]).ownerId).name;
}

const AP_MANAGER = getPerson('tom-gallagher').name;

function supplierName(a: InvoiceAnalysis): string {
  return getSupplier(a.invoice.supplierId).shortName;
}

function ccList(a: InvoiceAnalysis): string[] {
  const owner = ownerName(a);
  const names = [AP_MANAGER, owner];
  return names.filter((name, index) => names.indexOf(name) === index);
}

function itemCountText(count: number): string {
  if (count === 1) return 'One item does not match the agreement.';
  if (count === 2) return 'Two items do not match the agreement.';
  if (count === 3) return 'Three items do not match the agreement.';
  return `${count} items do not match the agreement.`;
}

function numberedItems(a: InvoiceAnalysis): string {
  return a.flagged
    .map((entry, index) => {
      const clause = entry.clauseId ? getClause(entry.clauseId) : undefined;
      const cite = clause ? ` (${clause.contractId} §${clause.section})` : '';
      return `${index + 1}. ${entry.label}${cite}\n   ${entry.supplierStatement}`;
    })
    .join('\n\n');
}

interface EmailOptions {
  subjectTail: string;
  closing: string;
}

function buildEmail(a: InvoiceAnalysis, options: EmailOptions): EmailDraft {
  const { invoice } = a;
  const supplier = getSupplier(invoice.supplierId);
  const contract = getContract(invoice.contractIds[0]);
  const intro = `We checked invoice ${invoice.id} (${usd(a.total)}, dated ${fmtDate(invoice.invoiceDate)}) against ${contract.id} before payment. ${itemCountText(a.flagged.length)}`;
  const body = [
    `Hello ${supplier.shortName} accounts receivable team,`,
    intro,
    numberedItems(a),
    options.closing,
    `Regards,\nAccounts Payable, ${COMPANY.name}\nSent by Gently on behalf of ${AP_MANAGER}`,
  ].join('\n\n');
  return {
    to: invoice.letterhead.arEmail,
    cc: ccList(a),
    subject: `Invoice ${invoice.id} (${invoice.po.id}): ${options.subjectTail}`,
    body,
  };
}

function thread(
  supplierAuthor: string,
  sent: string,
  acknowledged: [string, string],
  closed: [string, string],
): FollowUpScript {
  return {
    sent: {
      from: 'agent',
      author: 'Gently agent',
      body: sent,
      whenLabel: 'Just now',
    },
    acknowledged: [
      {
        from: 'supplier',
        author: supplierAuthor,
        body: acknowledged[0],
        whenLabel: '3 hours later',
      },
      {
        from: 'agent',
        author: 'Gently agent',
        body: acknowledged[1],
        whenLabel: '3 hours later',
      },
    ],
    closed: [
      {
        from: 'supplier',
        author: supplierAuthor,
        body: closed[0],
        whenLabel: '2 days later',
      },
      {
        from: 'agent',
        author: 'Gently agent',
        body: closed[1],
        whenLabel: '2 days later',
      },
    ],
  };
}

function stepList(
  entries: { label: string; detail: string }[],
): ScriptedStep[] {
  return entries.map((entry) => ({
    label: entry.label,
    detail: entry.detail,
    durationMs: STEP_MS,
  }));
}

function basePlan(
  a: InvoiceAnalysis,
): Pick<
  ResolutionPlan,
  | 'invoiceId'
  | 'payOn'
  | 'related'
  | 'autoReleased'
  | 'emailLabel'
  | 'email'
  | 'followUp'
> {
  return {
    invoiceId: a.invoice.id,
    payOn: a.contractDue,
    related: null,
    autoReleased: false,
    emailLabel: null,
    email: null,
    followUp: null,
  };
}

function corvanePlan(a: InvoiceAnalysis): ResolutionPlan {
  const price = impact(a, 'price');
  const freight = impact(a, 'freight');
  const held = a.summary.recoverable;
  const payNow = a.total - held;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const email = buildEmail(a, {
    subjectTail: 'dispute notice and request for corrected invoice',
    closing: `We will pay the undisputed amount of ${usd(payNow)} on ${date} and are holding ${usd(held)} on the disputed lines. Please send a corrected invoice within 5 business days. We opened dispute ${ref} in the supplier ledger and will follow up until it is closed.`,
  });
  return {
    ...basePlan(a),
    kind: 'dispute',
    tone: 'danger',
    headline: `Pay less than billed: hold ${usd(held)} and schedule ${usd(payNow)} for ${date} (Net 45 from receipt)`,
    rationale: `Send a dispute notice to ${supplierName(a)} billing citing §3.3, §4.2 and §5.1, and request a corrected invoice. The undisputed amount is paid on contract terms, ${a.termsGapDays} days after the date the invoice asks for.`,
    actions: [
      `Hold ${usd(price)} price variance and ${usd(freight)} freight`,
      `Schedule ${usd(payNow)} for ${date}`,
      `Send the dispute notice to ${a.invoice.letterhead.arEmail}`,
      'Track a corrected invoice until it passes every check',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Placing hold on disputed lines',
        detail: `2 lines held: ${usd(price)} price variance, ${usd(freight)} freight`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(payNow)} on ${date}, Net 45 from receipt`,
      },
      {
        label: `Sending dispute email to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `Dispute ${ref} opened for ${a.invoice.id}`,
      },
    ]),
    payNow,
    withheld: held,
    draftedLabel: 'Dispute drafted',
    sentLabel: 'Dispute sent, awaiting corrected invoice',
    closedLabel: 'Corrected invoice verified',
    outcome: `Dispute ${ref} opened. ${usd(payNow)} is scheduled for ${date}; ${usd(held)} is held until ${supplierName(a)} sends a corrected invoice.`,
    emailLabel: 'Edit dispute email',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Dispute notice sent to ${email.to}, citing §3.3, §4.2 and §5.1. Reminder scheduled for 2 business days if there is no reply.`,
      [
        `${supplierName(a)} billing acknowledged the dispute. Corrected invoice ${a.invoice.id}-R is expected in 2 days.`,
        `Reply logged against ${ref}. Reminder moved to day 2; ${usd(payNow)} stays scheduled for ${date}.`,
      ],
      [
        `Corrected invoice ${a.invoice.id}-R received for ${usd(payNow)}, Net 45.`,
        `Re-ran 9 checks on ${a.invoice.id}-R: all passed. It replaces ${a.invoice.id}. Dispute ${ref} closed and the ${usd(held)} hold released as a credit.`,
      ],
    ),
  };
}

function lumenPlan(a: InvoiceAnalysis): ResolutionPlan {
  const tariff = impact(a, 'duties');
  const payNow = a.total - tariff;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const email = buildEmail(a, {
    subjectTail: 'tariff surcharge line rejected, corrected invoice requested',
    closing: `We are paying the remaining ${usd(payNow)} on ${date}. Please send a corrected invoice without the surcharge within 5 business days. We opened dispute ${ref} in the supplier ledger and will follow up until it is closed.`,
  });
  return {
    ...basePlan(a),
    kind: 'dispute',
    tone: 'danger',
    headline: `Reject the ${usd(tariff)} tariff surcharge line and pay the ${usd(payNow)} balance on ${date}`,
    rationale:
      'The agreement has the supplier deliver with duties and tariffs paid (§5.1), and §5.3 makes every duty and tariff, including new ones, the supplier’s cost. The agent sends a rejection citing both clauses and asks for a corrected invoice. This is the same surcharge flagged in the risk alert.',
    actions: [
      `Hold the ${usd(tariff)} tariff line`,
      `Schedule ${usd(payNow)} for ${date} (Net 60)`,
      `Send the rejection to ${a.invoice.letterhead.arEmail}`,
      'Log the attempt against the supplier’s tariff pass-through history',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Placing hold on the tariff surcharge line',
        detail: `1 line held: ${usd(tariff)}`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(payNow)} on ${date}, Net 60 from receipt`,
      },
      {
        label: `Sending line rejection to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `Dispute ${ref} opened for ${a.invoice.id}`,
      },
    ]),
    payNow,
    withheld: tariff,
    draftedLabel: 'Line rejection drafted',
    sentLabel: 'Line rejected, awaiting corrected invoice',
    closedLabel: 'Corrected invoice verified',
    outcome: `Dispute ${ref} opened. ${usd(payNow)} is scheduled for ${date}; the ${usd(tariff)} tariff line is rejected pending a corrected invoice.`,
    emailLabel: 'Edit dispute email',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Line rejection sent to ${email.to}, citing §5.1 and §5.3. Reminder scheduled for 2 business days if there is no reply.`,
      [
        `${supplierName(a)} billing acknowledged. The tariff line will be withdrawn and corrected invoice ${a.invoice.id}-R issued in 2 days. They asked whether a price amendment can be discussed.`,
        `Price amendment request logged for ${ownerName(a)}. No change to the §5.3 position; ${ref} stays open.`,
      ],
      [
        `Corrected invoice ${a.invoice.id}-R received for ${usd(payNow)}, Net 60.`,
        `Re-ran 9 checks on ${a.invoice.id}-R: all passed. It replaces ${a.invoice.id}. Dispute ${ref} closed.`,
      ],
    ),
    related: { label: 'Risk alert RSK-305', alert: 'RSK-305' },
  };
}

function halbrookPlan(a: InvoiceAnalysis): ResolutionPlan {
  const rate = impact(a, 'rate_card');
  const nteOver = impact(a, 'not_to_exceed');
  const expenses = impact(a, 'expense_cap');
  const held = a.summary.recoverable;
  const payNow = a.total - held;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const owner = ownerName(a);
  const email = buildEmail(a, {
    subjectTail: 'dispute notice, change order and corrected invoice requested',
    closing: `We will pay the undisputed amount of ${usd(payNow)} on ${date} and are holding ${usd(held)}. Please send a corrected invoice at the agreed hourly rates with expenses within the cap, and a change order request for any work above the budget cap. We opened dispute ${ref} and will follow up until it is closed.`,
  });
  const cumulative =
    a.invoice.facts.cumulativeBilled?.['SOW-HALB-2026-03:5.2'] ?? 0;
  const nte = nteAmount();
  return {
    ...basePlan(a),
    kind: 'dispute',
    tone: 'danger',
    headline: `Hold ${usd(held)}, require a change order for the Senior Partner role, and flag the budget cap breach to ${owner}`,
    rationale: `Role rates ${usd(rate)}, budget cap ${usd(nteOver)} and expenses ${usd(expenses)} over the 10% cap. ${owner}, owner of the statement of work, is told that billing reached ${usd(cumulative + a.total)} against the ${usd(nte)} cap. The undisputed ${usd(payNow)} is scheduled on Net 60.`,
    actions: [
      `Hold ${usd(held)} across three findings`,
      `Schedule ${usd(payNow)} for ${date}`,
      `Notify ${owner} of the budget cap breach`,
      'Request a change order and a corrected invoice',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Placing hold on disputed amounts',
        detail: `${usd(held)}: role rates ${usd(rate)}, budget cap ${usd(nteOver)}, expenses ${usd(expenses)}`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(payNow)} on ${date}, Net 60`,
      },
      {
        label: 'Flagging budget cap breach to the statement of work owner',
        detail: `${owner} notified: ${usd(cumulative + a.total)} billed against ${usd(nte)}`,
      },
      {
        label: `Sending dispute email to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `Dispute ${ref} opened for ${a.invoice.id}`,
      },
    ]),
    payNow,
    withheld: held,
    draftedLabel: 'Dispute drafted',
    sentLabel: 'Dispute sent, awaiting corrected invoice',
    closedLabel: 'Corrected invoice verified',
    outcome: `Dispute ${ref} opened. ${usd(payNow)} is scheduled for ${date}; ${usd(held)} is held. ${owner} has the budget cap breach and the change order request.`,
    emailLabel: 'Edit dispute email',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Dispute notice sent to ${email.to}. ${owner} notified of the budget cap breach. Reminder scheduled for 2 business days.`,
      [
        `${supplierName(a)} billing acknowledged. Corrected invoice ${a.invoice.id}-R at the agreed rates is expected in 2 days, and a change order request has gone to the engagement partner.`,
        `Change order request logged against SOW-HALB-2026-03 and routed to ${owner} for decision. ${usd(payNow)} stays scheduled.`,
      ],
      [
        `Corrected invoice ${a.invoice.id}-R received for ${usd(payNow)}, Net 60.`,
        `Re-ran 9 checks on ${a.invoice.id}-R: all passed, with cumulative billing at ${usd(cumulative + payNow)} of ${usd(nte)}. Dispute ${ref} closed.`,
      ],
    ),
  };
}

function transconPlan(a: InvoiceAnalysis): ResolutionPlan {
  const fuel = impact(a, 'surcharge');
  const waiting = impact(a, 'proof');
  const held = a.summary.recoverable;
  const payNow = a.total - held;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const email = buildEmail(a, {
    subjectTail: 'fuel surcharge over cap, waiting-time proof requested',
    closing: `We will pay ${usd(payNow)} on ${date}. Please send a corrected invoice with the fuel surcharge at the cap, and the signed proof for the waiting-time charge. The waiting-time charge stays on hold until the proof arrives. We opened dispute ${ref} and will follow up until it is closed.`,
  });
  return {
    ...basePlan(a),
    kind: 'dispute',
    tone: 'danger',
    headline: `Pay ${usd(fuel)} less for the fuel overage and hold ${usd(waiting)} of waiting time pending signed proof (§5.4)`,
    rationale: `The fuel surcharge is 24.0% of base freight against the 18% cap in §5.2. Waiting time is payable only with proof signed by a Pillar facility representative, and none is on file. The remaining ${usd(payNow)} is scheduled on Net 45.`,
    actions: [
      `Pay ${usd(fuel)} less for the fuel overage`,
      `Hold ${usd(waiting)} of waiting time until signed proof arrives`,
      `Schedule ${usd(payNow)} for ${date}`,
      'Request a corrected invoice and the signed gate record',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Paying less for the fuel surcharge overage',
        detail: `${usd(fuel)} above the 18% cap`,
      },
      {
        label: 'Holding waiting time pending signed proof',
        detail: `${usd(waiting)} held, load RT-40571`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(payNow)} on ${date}, Net 45`,
      },
      {
        label: `Sending dispute email to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `Dispute ${ref} opened for ${a.invoice.id}`,
      },
    ]),
    payNow,
    withheld: held,
    draftedLabel: 'Partial payment drafted',
    sentLabel: 'Paid less than billed, awaiting corrected invoice',
    closedLabel: 'Corrected invoice verified',
    outcome: `Dispute ${ref} opened. ${usd(payNow)} is scheduled for ${date}; ${usd(fuel)} of the fuel overage is not paid and ${usd(waiting)} of waiting time is on hold.`,
    emailLabel: 'Edit dispute email',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Dispute notice sent to ${email.to}, citing §5.2 and §5.4. Reminder scheduled for 2 business days.`,
      [
        `${supplierName(a)} billing acknowledged the fuel cap. Corrected invoice ${a.invoice.id}-R is expected in 2 days; the signed gate record for load RT-40571 will follow separately.`,
        `Reply logged against ${ref}. Waiting-time hold stays open; reminder set for 5 business days if proof has not arrived.`,
      ],
      [
        `Corrected invoice ${a.invoice.id}-R received for ${usd(payNow)} (base freight plus fuel at the 18% cap), Net 45.`,
        `Re-ran 8 checks on ${a.invoice.id}-R: all passed. Waiting time of ${usd(waiting)} stays on hold until signed proof arrives.`,
      ],
    ),
  };
}

function stratusPlan(a: InvoiceAnalysis): ResolutionPlan {
  const credit = a.summary.recoverable;
  const payNow = a.total - credit;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const email = buildEmail(a, {
    subjectTail:
      'uptime credit for September availability, credit memo requested',
    closing: `Please issue a credit memo for ${usd(credit)} within 5 business days. We will schedule payment of the net amount, ${usd(payNow)}, for ${date}. We opened request ${ref} in the supplier ledger and will follow up until the credit memo arrives.`,
  });
  return {
    ...basePlan(a),
    kind: 'credit_request',
    tone: 'warning',
    headline: `Request the ${usd(credit)} uptime credit memo before paying, then schedule ${usd(payNow)} for ${date}`,
    rationale: `September availability was 99.81% against the 99.95% commitment in §6.2, which earns a 10% credit on the ${usd(a.total)} monthly fee. §6.3 says the credit is applied automatically, so it should already be on this invoice. Payment is scheduled on contract terms, net of the credit.`,
    actions: [
      `Request a credit memo for ${usd(credit)}`,
      `Schedule ${usd(payNow)} for ${date} (Net 45)`,
      'Record the credit owed to Pillar in the supplier ledger',
      'Track the credit memo until it arrives',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: `Verifying availability against ${supplierName(a)} uptime measurements`,
        detail: 'September 2026: 99.81% against 99.95%',
      },
      {
        label: `Sending credit memo request to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Scheduling net payment on contract terms',
        detail: `${usd(payNow)} on ${date}, Net 45`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `${usd(credit)} credit owed to Pillar logged (${ref})`,
      },
    ]),
    payNow,
    withheld: credit,
    draftedLabel: 'Credit request drafted',
    sentLabel: 'Credit memo requested',
    closedLabel: 'Credit memo received',
    outcome: `Credit request ${ref} sent. ${usd(payNow)} is scheduled for ${date}, net of the ${usd(credit)} credit.`,
    emailLabel: 'Edit credit request',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Credit request sent to ${email.to}, citing §6.2 and §6.3. Reminder scheduled for 2 business days.`,
      [
        `${supplierName(a)} billing acknowledged. Credit memo CM-2026-3318 for ${usd(credit)} will be issued within 3 business days.`,
        `Credit of ${usd(credit)} owed to Pillar recorded against ${ref}.`,
      ],
      [
        `Credit memo CM-2026-3318 received for ${usd(credit)}.`,
        `Applied to ${a.invoice.id}. Net payable ${usd(payNow)} stays scheduled for ${date}. Request ${ref} closed.`,
      ],
    ),
  };
}

function keystonePlan(a: InvoiceAnalysis): ResolutionPlan {
  const markup = impact(a, 'markup');
  const overtime = impact(a, 'proof');
  const held = a.summary.recoverable;
  const payNow = a.total - held;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const email = buildEmail(a, {
    subjectTail:
      'markup over cap and unapproved overtime, corrected invoice requested',
    closing: `We will pay ${usd(payNow)} on ${date} and are holding ${usd(held)}. Please send a corrected invoice at the capped rates, together with the written overtime approval or without the overtime hours. We opened dispute ${ref} and will follow up until it is closed.`,
  });
  return {
    ...basePlan(a),
    kind: 'dispute',
    tone: 'warning',
    headline: `Pay ${usd(held)} less than billed (${usd(markup)} markup over the 32% cap and ${usd(overtime)} unapproved overtime) and request a corrected invoice`,
    rationale:
      'Both findings depend on data the supplier controls (pay rates in the assignment letters and overtime approvals), so they are warnings. The agent pays less than billed and pays the rest on time rather than holding the whole invoice.',
    actions: [
      `Hold ${usd(held)}`,
      `Schedule ${usd(payNow)} for ${date}`,
      `Send the notice to ${a.invoice.letterhead.arEmail}`,
      'Track a corrected invoice or the overtime approval',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Holding disputed amounts',
        detail: `${usd(markup)} markup over cap, ${usd(overtime)} overtime`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(payNow)} on ${date}, Net 45`,
      },
      {
        label: `Sending dispute email to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `Dispute ${ref} opened for ${a.invoice.id}`,
      },
    ]),
    payNow,
    withheld: held,
    draftedLabel: 'Partial payment drafted',
    sentLabel: 'Paid less than billed, awaiting corrected invoice',
    closedLabel: 'Corrected invoice verified',
    outcome: `Dispute ${ref} opened. ${usd(payNow)} is scheduled for ${date}; ${usd(held)} is held until ${supplierName(a)} corrects the invoice.`,
    emailLabel: 'Edit dispute email',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Dispute notice sent to ${email.to}, citing §2.1 and §2.2. Reminder scheduled for 2 business days.`,
      [
        `${supplierName(a)} billing acknowledged. They will reissue at the capped rates and either send the overtime approval or remove the hours.`,
        `Reply logged against ${ref}. ${usd(payNow)} stays scheduled for ${date}.`,
      ],
      [
        `Corrected invoice ${a.invoice.id}-R received for ${usd(payNow)}, Net 45.`,
        `Re-ran 8 checks on ${a.invoice.id}-R: all passed. It replaces ${a.invoice.id}. Dispute ${ref} closed.`,
      ],
    ),
  };
}

function northgatePlan(a: InvoiceAnalysis): ResolutionPlan {
  const rebate = a.summary.recoverable;
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const facts = a.invoice.facts.rebate;
  const ytd = facts?.ytdNetPurchases ?? 0;
  const email = buildEmail(a, {
    subjectTail: 'volume rebate credit memo requested',
    closing: `The invoice itself is correct and will be paid ${usd(a.total)} on ${date}. Please issue a credit memo for ${usd(rebate)} within 5 business days. We opened request ${ref} in the supplier ledger and will follow up until the credit memo arrives.`,
  });
  return {
    ...basePlan(a),
    kind: 'credit_request',
    tone: 'warning',
    headline: `Request the ${usd(rebate)} volume rebate credit memo and pay this invoice on Net 30 terms (${date})`,
    rationale: `Year-to-date purchases reached ${usd(ytd)}, passing the ${usd(rebateThreshold())} tier in §2.2. The rebate is 1.5% of those purchases and no credit note has been issued. The invoice itself is correct, so payment is not held.`,
    actions: [
      `Request a credit memo for ${usd(rebate)}`,
      `Schedule ${usd(a.total)} for ${date} (Net 30)`,
      'Record the rebate owed to Pillar in the supplier ledger',
      'Track the credit memo until it arrives',
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Calculating the rebate from year-to-date purchases',
        detail: `1.5% x ${usd(ytd)} = ${usd(rebate)}`,
      },
      {
        label: `Sending credit memo request to ${supplierName(a)} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(a.total)} on ${date}, Net 30`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `${usd(rebate)} rebate owed to Pillar logged (${ref})`,
      },
    ]),
    payNow: a.total,
    withheld: 0,
    draftedLabel: 'Credit request drafted',
    sentLabel: 'Credit memo requested',
    closedLabel: 'Credit memo received',
    outcome: `Credit request ${ref} sent for ${usd(rebate)}. The invoice is scheduled for ${date} at the full ${usd(a.total)}.`,
    emailLabel: 'Edit credit request',
    email,
    followUp: thread(
      `${supplierName(a)} billing`,
      `Credit request sent to ${email.to}, citing §2.2. Reminder scheduled for 2 business days.`,
      [
        `${supplierName(a)} billing confirmed the year-to-date volume. Credit note CN-2026-0418 for ${usd(rebate)} will be issued within 5 business days.`,
        `Rebate of ${usd(rebate)} owed to Pillar recorded against ${ref}. Payment of ${usd(a.total)} is unchanged.`,
      ],
      [
        `Credit note CN-2026-0418 received for ${usd(rebate)}.`,
        `Credit applied against ${supplierName(a)}’s next remittance. Request ${ref} closed.`,
      ],
    ),
  };
}

function aureliaPlan(a: InvoiceAnalysis): ResolutionPlan {
  const early = a.results.find((entry) => entry.category === 'early_pay');
  const saving = early?.opportunity ?? 0;
  const deadline = earlyPayDeadline(a.invoice) ?? a.contractDue;
  const payNow = a.total - saving;
  const date = fmtDate(deadline);
  return {
    ...basePlan(a),
    kind: 'early_pay',
    tone: 'success',
    headline: `Pay early on day 10 to capture ${usd(saving)} (2% discount)`,
    rationale: `All ${a.summary.total} checks passed. ${early?.explanation ?? ''} Otherwise the invoice is released on ${fmtDate(a.contractDue)}.`,
    actions: [
      `Schedule ${usd(payNow)} for ${date}`,
      `Capture the ${usd(saving)} discount`,
      'Log the discount in the supplier ledger',
    ],
    primaryLabel: 'Schedule early payment',
    steps: stepList([
      {
        label: 'Confirming the discount window',
        detail: `Open until ${date}`,
      },
      {
        label: 'Scheduling early payment',
        detail: `${usd(payNow)} on ${date}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `${usd(saving)} early-pay discount captured`,
      },
    ]),
    payNow,
    withheld: 0,
    payOn: deadline,
    draftedLabel: 'Early-pay available',
    sentLabel: 'Early payment scheduled',
    closedLabel: 'Early payment scheduled',
    outcome: `${usd(payNow)} is scheduled for ${date}, capturing the ${usd(saving)} discount.`,
  };
}

function releasedPlan(a: InvoiceAnalysis): ResolutionPlan {
  const date = fmtDate(a.contractDue);
  return {
    ...basePlan(a),
    kind: 'released',
    tone: 'success',
    headline: 'All checks passed. Released to the next payment run',
    rationale: `${usd(a.total)} is scheduled for ${date} on contract terms (${a.invoice.statedTerms.label}). Nothing is held.`,
    actions: [],
    primaryLabel: 'Release to payment run',
    steps: [],
    payNow: a.total,
    withheld: 0,
    autoReleased: true,
    draftedLabel: 'Released to payment run',
    sentLabel: 'Released to payment run',
    closedLabel: 'Released to payment run',
    outcome: `${usd(a.total)} is scheduled for ${date}.`,
  };
}

/**
 * Border States bills Net 30 against Net 45 in the agreement. Price and
 * quantity match the PO, so nothing is held: the agent schedules payment on the
 * contract terms and tells the supplier. This runs when the invoice is
 * ingested, so the queue seeds it as already resolved (see `auto-resolved.ts`).
 */
function termsCorrectedPlan(a: InvoiceAnalysis): ResolutionPlan {
  const terms = a.flagged.find((entry) => entry.category === 'terms');
  const clause = terms?.clauseId ? getClause(terms.clauseId) : undefined;
  const cite = clause ? `§${clause.section}` : 'the agreement';
  const ref = DISPUTE_REF[a.invoice.id];
  const date = fmtDate(a.contractDue);
  const supplier = supplierName(a);
  const netDays = Math.round(
    (a.contractDue.getTime() - a.invoice.invoiceDate.getTime()) / 86_400_000,
  );
  const prices = a.results.filter((entry) => entry.category === 'price').length;
  const email = buildEmail(a, {
    subjectTail: `payment terms are Net ${netDays} under the agreement`,
    closing: `No change is needed on the invoice itself. The full ${usd(a.total)} is scheduled for ${date}, Net ${netDays} from receipt, and nothing is held. Please update the payment terms on your billing profile so the next invoices print Net ${netDays}. Reference ${ref}.`,
  });
  return {
    ...basePlan(a),
    kind: 'terms_corrected',
    tone: 'success',
    headline: `Payment scheduled on contract terms (Net ${netDays}), no hold needed`,
    rationale: `Prices and quantities match ${a.invoice.po.id} and the receipt, so only the printed terms are off. ${cite} sets Net ${netDays} and a shorter term on an invoice has no effect: ${usd(a.total)} is scheduled for ${date}, ${a.termsGapDays} days after the date the invoice asks for.`,
    actions: [
      `Schedule ${usd(a.total)} for ${date} (Net ${netDays})`,
      `Send the terms notice to ${a.invoice.letterhead.arEmail}`,
      `Ask ${supplier} to correct the terms on its billing profile`,
    ],
    primaryLabel: 'Approve recommendation',
    steps: stepList([
      {
        label: 'Checking price and quantity',
        detail: `${prices} prices match the agreement and quantities match ${a.invoice.po.id} and ${a.invoice.receipt.id}`,
      },
      {
        label: 'Scheduling payment on contract terms',
        detail: `${usd(a.total)} on ${date}, Net ${netDays} from receipt`,
      },
      {
        label: `Sending terms notice to ${supplier} billing`,
        detail: `${email.to}, cc ${email.cc.join(' and ')}`,
      },
      {
        label: 'Logging in supplier ledger',
        detail: `Terms correction ${ref} recorded for ${a.invoice.id}`,
      },
    ]),
    payNow: a.total,
    withheld: 0,
    draftedLabel: 'Terms notice drafted',
    sentLabel: 'Terms notice sent',
    closedLabel: `Paid on Net ${netDays}, terms corrected`,
    outcome: `Payment scheduled on contract terms (Net ${netDays}). ${usd(a.total)} is scheduled for ${date}; nothing is held because price and quantity match.`,
    emailLabel: 'Edit terms notice',
    email,
    followUp: thread(
      `${supplier} billing`,
      `Terms notice sent to ${email.to}, citing ${cite}. Payment scheduled for ${date} on Net ${netDays}.`,
      [
        `${supplier} billing confirmed Net ${netDays} and will update its billing profile.`,
        `Reply logged against ${ref}. ${usd(a.total)} stays scheduled for ${date}.`,
      ],
      [
        `${supplier} billing updated the terms on its profile. The next invoice prints Net ${netDays}.`,
        `Verified in the supplier record. ${ref} closed.`,
      ],
    ),
  };
}

/**
 * Brazos Valley's crating line is not on the PO, but the agreement allows it as
 * the one accessorial and the signed work order is on file, so it is payable.
 */
function accessorialPlan(a: InvoiceAnalysis): ResolutionPlan {
  const date = fmtDate(a.contractDue);
  const line = a.invoice.lines.find((entry) => entry.kind === 'accessorial');
  const clause = a.results
    .map((entry) => (entry.clauseId ? getClause(entry.clauseId) : undefined))
    .find((entry) => entry?.kind === 'accessorials');
  const cite = clause ? `§${clause.section}` : 'the agreement';
  const proof = line?.evidence?.ref ? ` ${line.evidence.ref}` : '';
  return {
    ...basePlan(a),
    kind: 'released',
    tone: 'success',
    headline: `All checks passed. The ${usd(line?.amount ?? 0)} crating line is an allowed extra charge`,
    rationale: `Crating is not on ${a.invoice.po.id}, but ${cite} allows it as the one extra charge and the signed work order${proof} is on file. ${usd(a.total)} is scheduled for ${date} on contract terms (${a.invoice.statedTerms.label}). Nothing is held.`,
    actions: [],
    primaryLabel: 'Release to payment run',
    steps: [],
    payNow: a.total,
    withheld: 0,
    autoReleased: true,
    draftedLabel: 'Released to payment run',
    sentLabel: 'Released to payment run',
    closedLabel: 'Released to payment run',
    outcome: `${usd(a.total)} is scheduled for ${date}.`,
  };
}

/** Sentinel's freight follows the AP payment run: one day before the Net 15 due date. */
function freightPlan(a: InvoiceAnalysis): ResolutionPlan {
  const payOn = addDays(a.contractDue, -1);
  const date = fmtDate(payOn);
  return {
    ...releasedPlan(a),
    rationale: `The flat route rate and the Net 15 terms match the agreement, and no fuel surcharge or waiting time is billed. ${usd(a.total)} is scheduled for ${date}, one day before the due date. Nothing is held.`,
    payOn,
    outcome: `${usd(a.total)} is scheduled for ${date}.`,
  };
}

function buildPlan(a: InvoiceAnalysis): ResolutionPlan {
  switch (a.invoice.id) {
    case 'CV-2026-08812':
      return corvanePlan(a);
    case 'LB-26-3391':
      return lumenPlan(a);
    case 'HP-INV-55102':
      return halbrookPlan(a);
    case 'TCF-778120':
      return transconPlan(a);
    case 'STR-INV-20931':
      return stratusPlan(a);
    case 'KS-104477':
      return keystonePlan(a);
    case 'NGF-22871':
      return northgatePlan(a);
    case 'AFP-90233':
    case 'AIG-90412':
      return aureliaPlan(a);
    case 'BS-22188':
      return termsCorrectedPlan(a);
    case 'BVL-7741':
      return accessorialPlan(a);
    case 'SF-3398':
      return freightPlan(a);
    case 'GAI-2026-0912':
    case 'BLC-3318':
      return releasedPlan(a);
  }
}

let planCache: Record<InvoiceId, ResolutionPlan> | null = null;

export function getPlans(): Record<InvoiceId, ResolutionPlan> {
  if (planCache === null) {
    const analyses = getAnalyses();
    planCache = {
      'CV-2026-08812': buildPlan(analyses['CV-2026-08812']),
      'LB-26-3391': buildPlan(analyses['LB-26-3391']),
      'HP-INV-55102': buildPlan(analyses['HP-INV-55102']),
      'TCF-778120': buildPlan(analyses['TCF-778120']),
      'STR-INV-20931': buildPlan(analyses['STR-INV-20931']),
      'KS-104477': buildPlan(analyses['KS-104477']),
      'NGF-22871': buildPlan(analyses['NGF-22871']),
      'GAI-2026-0912': buildPlan(analyses['GAI-2026-0912']),
      'AFP-90233': buildPlan(analyses['AFP-90233']),
      'BLC-3318': buildPlan(analyses['BLC-3318']),
      'AIG-90412': buildPlan(analyses['AIG-90412']),
      'BS-22188': buildPlan(analyses['BS-22188']),
      'BVL-7741': buildPlan(analyses['BVL-7741']),
      'SF-3398': buildPlan(analyses['SF-3398']),
    };
  }
  return planCache;
}

export function getPlan(id: InvoiceId): ResolutionPlan {
  return getPlans()[id];
}

export const OVERRIDE_REASONS: { value: string; label: string }[] = [
  {
    value: 'supply_continuity',
    label: 'Supply at risk: running out of stock or a customer order delayed',
  },
  {
    value: 'amendment_pending',
    label: 'Price or terms amendment pending signature',
  },
  { value: 'dispute_open', label: 'Duplicate of a dispute already open' },
  { value: 'sponsor_instruction', label: 'Instruction from executive sponsor' },
  { value: 'other', label: 'Other (explain in the note)' },
];

export function overrideReasonLabel(value: string): string {
  return (
    OVERRIDE_REASONS.find((reason) => reason.value === value)?.label ?? value
  );
}
